import { Box, Button, Paper, Tab, Tabs } from "@mui/material";
import { CalendarToday, LocalHospital, Person, WhatsApp } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TabPanel from "../../../../components/common/tab-panel/TabPanel";
import SearchHospital from "./HospitalRefer";
import HospitalReferredPage from "./RefferedUserList";
import PageHead from "../../../../components/common/page/PageHead";
import { formatCampLabel } from "../../../../utils/campLabel";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import { openDrawer } from "../../../../redux/reducer/drawerSlice";
import { showSnackbar } from "../../../../redux/reducer/snackbarSlice";
import { FetchReferalLetterListService } from "../../../../utils/services/beneficiary.activity.service";
import { Referral } from "../../../../utils/dto/module/campaign.activity";
import { clearModuleRefresh } from "../../../../redux/reducer/refreshSlice";
import { usePermissions } from "../../../../providers/PermissionProvider";
import NoPermissionMessage from "../../../../components/common/NoPermissionMessage";
import PageContent from "../../../../components/common/page/PageContent";
import { useTranslation } from "../../../../hooks/useTranslation";

const ReferHospitalTabComponent: React.FC = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [value, setValue] = useState<number | null>(0);
    const refresh = useSelector((state: RootState) => state.refresh.referal);
    const { checkPermission } = usePermissions();
    const isViewThisPage: boolean = checkPermission('view-referral-letter');

    const { selectedCamp, searchTerm } = useSelector((state: RootState) => state.filters);
    const selectedHospitals = useSelector((state: any) => state.hospitalSelection.selectedHospitals);
    const [beneficiaries, setBeneficiaries] = useState<Referral[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [payload, setPayload] = useState<{ offset: number; limit: number }>({
        offset: 0,
        limit: 15,
    });

    const [searchParams] = useSearchParams();
    const isDashboardView = searchParams.get('nav') === 'dashboard';

    // ✅ FIXED: Removed 't' from dependencies to avoid infinite loop
    const fetchReferalLetterList = useCallback(async () => {
        if (!isViewThisPage || !selectedCamp?.uuid) return;
        setLoading(true);
        const reqData: Record<string, any> = {
            limit: payload.limit,
            offset: payload.offset,
            campaign_uuid: selectedCamp?.uuid || '',
            search: searchTerm,
            ...(!isDashboardView && { type: 'completed' }),
        };
        const { code, data } = await FetchReferalLetterListService(reqData);
        if (code === 200 && data && data.data) {
            setBeneficiaries(data.data);
            setTotalCount(data.filtered_count);
        } else {
            setBeneficiaries([]);
            setTotalCount(0);
        }
        dispatch(clearModuleRefresh({ moduleName: 'referal' }));
        setLoading(false);
    }, [dispatch, selectedCamp?.uuid, searchTerm, payload.limit, payload.offset, refresh, isViewThisPage, isDashboardView]);

    useEffect(() => {
        fetchReferalLetterList();
    }, [fetchReferalLetterList]);

    // ✅ FIXED: Move useMemo BEFORE conditional return
    // Tabs configuration with translation - useMemo to update when language changes
    const tabs = useMemo(() => [
        {
            label: t('referHospital', 'hospitalRefer'),
            icon: <LocalHospital fontSize="small" />,
            isDisabled: false,
            type: 'hospital_refer',
            component: <SearchHospital />,
        },
        {
            label: t('referHospital', 'referredBeneficiary'),
            icon: <Person fontSize="small" />,
            isDisabled: false,
            type: 'referred_beneficiary',
            component: <HospitalReferredPage
                beneficiaries={beneficiaries || []}
                loading={loading}
                totalCount={totalCount}
                setPayload={setPayload}
                payload={payload}
            />,
        },
    ], [t, beneficiaries, loading, totalCount, payload]);

    // ✅ Build page title with translation (also before conditional return)
    const campName = selectedCamp ? formatCampLabel(selectedCamp) : t('referHospital', 'allCampaigns');
    const pageTitle = `${t('referHospital', 'referralLetterFor')} ${campName}`;

    const handleSMSClick = () => {
        if (!selectedHospitals || selectedHospitals.length === 0) {
            dispatch(showSnackbar({
                type: "error",
                message: t('referHospital', 'selectHospitalFirst')
            }));
            return;
        }
        dispatch(openDrawer({
            title: t('referHospital', 'sendReferralLetterDrawer'),
            _Key: 'sendHospitalDetails',
            isFullScreen: false,
            drawerData: { mobile: '9370454334' },
            drawerWidth: 'lg'
        }));
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    // ✅ Conditional return AFTER all hooks
    if (!isViewThisPage) {
        return <NoPermissionMessage height='calc(100vh - 59px)' />;
    }

    if (value === null) return (<></>);

    return (
        <PageContent>
            <PageHead
                primary={pageTitle}
                back={<CalendarToday />}
                secondary={
                    <>
                        {(checkPermission('add-referral-letter') && value === 0) && (
                            <Button
                                color="primary"
                                variant="outlined"
                                // startIcon={<WhatsApp color="success" />}
                                onClick={() => handleSMSClick()}
                            >
                                {t('referHospital', 'sendReferralLetter')} ({selectedHospitals?.length ?? 0})
                            </Button>
                        )}
                    </>
                }
            />
            <Paper sx={{ boxShadow: 3, mt: 0.5, height: "100%" }}>
                <SearchFilterBar totalItems={beneficiaries?.length ?? 0} />
                <Box sx={{ borderBottom: 1, borderColor: 'grey.400', borderRadius: '8px 8px 0 0' }}>
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons={false}
                        allowScrollButtonsMobile
                        aria-label="scrollable force tabs gmail"
                    >
                        {tabs.map((tab, index) => (
                            <Tab
                                key={`${tab.type}-${index}`}
                                sx={{ py: 0, px: 1.3, fontSize: '13px', fontWeight: 600, minHeight: 48 }}
                                icon={tab.icon}
                                disabled={tab.isDisabled}
                                iconPosition="start"
                                label={tab.label}
                            />
                        ))}
                    </Tabs>
                </Box>
                <Box py={0} px={0}>
                    {tabs.map((tab, index) => (
                        <TabPanel key={`${tab.type}-tabpanel-${index}`} value={value} index={index}>
                            {tab.component}
                        </TabPanel>
                    ))}
                </Box>
            </Paper>
        </PageContent>
    );
};

export default ReferHospitalTabComponent;
